import { useEffect, useMemo, useRef, useState } from 'react';
import { useDebouncedEffect } from '@react-hookz/web';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Network } from 'lucide-react';
import z from 'zod';

import type { RecentExecutionsFilters, WorkflowListFilters } from '@/api/endpoints/loom';
import { useListRecentWorkflowExecutionsV2Infinite, useListWorkflowsV2Filtered } from '@/api/endpoints/loom';
import {
  TLSortDirection,
  TLWfExecutionStatus,
  TLWfOwnershipScope,
  TLWfTriggerType,
  TLWfWorkflowSortField,
  TLWfWorkflowStatus,
} from '@/api/gen/tessera-backend-api.schemas';
import { useListTemplatesV2 } from '@/api/gen/workflows-v2-templates';
import { prefetchListWorkflowsV2Query } from '@/api/gen/workflows-v2-workflows';
import type { ExecutionFilterValues } from '@/components/features/loom/components/LoomExecutionsFilterBar';
import type { WorkflowFilterValues } from '@/components/features/loom/components/WorkflowsFilterBar';
import { LoomExecutionsTable } from '@/components/features/loom/LoomExecutionsTable';
import { TemplatesTable } from '@/components/features/loom/TemplatesTable';
import {
  ExecutionStartedWithin,
  isValidDateParam,
  localDateEndIso,
  localDateStartIso,
  startedWithinCutoffIso,
} from '@/components/features/loom/utils/execution';
import {
  LOOM_TAB_DESCRIPTION,
  LoomTab,
  OWNERSHIP_SCOPE,
  parseLoomTab,
  type WorkflowOwnershipFilterValue,
} from '@/components/features/loom/utils/workflow-list';
import type { WorkflowSortState } from '@/components/features/loom/WorkflowsTable';
import { WorkflowsTable } from '@/components/features/loom/WorkflowsTable';
import { EDITOR_STORAGE_KEYS, TLAI_FEATURE_FLAGS } from '@/constants';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useUserPermissions } from '@/hooks/use-user-permissions';
import { AccessDenied } from '@/modus/AccessDenied';
import { FeatureHeader } from '@/shared/FeatureHeader';
import { TLAIDomain } from '@/types/api';
import { PanelTabs } from '@/ui/panel-tabs/PanelTabs';
import { getEditorStorageKey } from '@/utils/editor-storage';

const parseOwnership = (value: string | null): WorkflowOwnershipFilterValue | null =>
  value === 'all' || value === 'owned' || value === 'shared' ? value : null;

const SEARCH_DEBOUNCE_MS = 300;
const WORKFLOWS_PAGE_SIZE = 50;
// Short cache window so toggling filters back and forth (and hover prefetches) resolve instantly.
const WORKFLOWS_LIST_STALE_MS = 30_000;

const dateParam = z.string().refine(isValidDateParam);

export const Route = createFileRoute('/_protected/t/$tenantId/loom/')({
  component: WorkflowListPage,
  validateSearch: z.object({
    tab: z.nativeEnum(LoomTab).optional().catch(undefined),
    owner: z.enum(['all', 'owned', 'shared']).optional().catch(undefined),
    tmplOwner: z.enum(['all', 'owned', 'shared']).optional().catch(undefined),
    tmplBy: z.array(z.string()).optional().catch(undefined),
    wfQ: z.string().optional().catch(undefined),
    wfStatus: z.nativeEnum(TLWfWorkflowStatus).optional().catch(undefined),
    wfArchived: z.boolean().optional().catch(undefined),
    wfBy: z.array(z.string()).optional().catch(undefined),
    wfSort: z.nativeEnum(TLWfWorkflowSortField).optional().catch(undefined),
    wfDir: z.nativeEnum(TLSortDirection).optional().catch(undefined),
    wfPage: z.coerce.number().int().positive().optional().catch(undefined),
    execStatus: z.nativeEnum(TLWfExecutionStatus).optional().catch(undefined),
    execTrigger: z.nativeEnum(TLWfTriggerType).optional().catch(undefined),
    execWorkflow: z.guid().optional().catch(undefined),
    execBy: z.string().optional().catch(undefined),
    execWithin: z.nativeEnum(ExecutionStartedWithin).optional().catch(undefined),
    execQ: z.string().optional().catch(undefined),
    execFrom: dateParam.optional().catch(undefined),
    execTo: dateParam.optional().catch(undefined),
  }),
});

const LoomTabs = PanelTabs<LoomTab>;

// The keycloak/user role is the hard ceiling: without the workflow-view role
// the page is denied even if workflows were shared with the user directly.
// Splitting the gate from the content keeps the data queries from firing for
// users who can't view workflows.
function WorkflowListPage() {
  const { hasPermission } = useUserPermissions();

  if (!hasPermission('workflows.workflow.view')) {
    return (
      <div className="flex size-full items-center justify-center">
        <AccessDenied description="You don't have permission to view workflows. Please contact your administrator if you believe this is an error." />
      </div>
    );
  }

  return <WorkflowList />;
}

function WorkflowList() {
  const navigate = Route.useNavigate();
  const { tenantId } = Route.useParams();
  // The new navigation's sidebar owns tab switching for this landing page.
  const newNavigationEnabled = useFeatureFlag(TLAI_FEATURE_FLAGS.NEW_NAVIGATION, false);
  const {
    tab: tabParam,
    owner: ownerParam,
    tmplOwner: tmplOwnerParam,
    tmplBy,
    wfQ,
    wfStatus: wfStatusParam,
    wfArchived,
    wfBy,
    wfSort,
    wfDir,
    wfPage,
    execStatus,
    execTrigger,
    execWorkflow,
    execBy,
    execWithin,
    execFrom,
    execTo,
    execQ,
  } = Route.useSearch();

  const [workflowSearch, setWorkflowSearch] = useState(wfQ ?? '');
  const [debouncedWorkflowSearch, setDebouncedWorkflowSearch] = useState((wfQ ?? '').trim());
  // Term last reconciled between the input and the URL. Both effects below skip a
  // change they caused themselves, so the local→URL write and the URL→local sync
  // can't ping-pong and clobber in-flight keystrokes.
  const lastSyncedWorkflowQueryRef = useRef((wfQ ?? '').trim());

  useDebouncedEffect(
    () => {
      const trimmed = workflowSearch.trim();
      // Reset pagination only when the term actually changed. Otherwise the effect
      // firing on mount would wipe wfPage from a deep link like ?wfQ=foo&wfPage=3.
      if (trimmed === lastSyncedWorkflowQueryRef.current) return;
      lastSyncedWorkflowQueryRef.current = trimmed;
      setDebouncedWorkflowSearch(trimmed);
      navigate({
        to: '/t/$tenantId/loom',
        params: { tenantId },
        search: (prev) => ({ ...prev, wfQ: trimmed || undefined, wfPage: undefined }),
        replace: true,
      });
    },
    [workflowSearch],
    SEARCH_DEBOUNCE_MS,
  );

  // Mirror wfQ back into the input when it changes outside of typing (browser
  // back/forward, external navigation); leaves wfPage untouched.
  useEffect(() => {
    const urlQuery = (wfQ ?? '').trim();
    if (urlQuery === lastSyncedWorkflowQueryRef.current) return;
    lastSyncedWorkflowQueryRef.current = urlQuery;
    setWorkflowSearch(wfQ ?? '');
    setDebouncedWorkflowSearch(urlQuery);
  }, [wfQ]);

  const [executionSearch, setExecutionSearch] = useState(execQ ?? '');
  const [debouncedExecutionSearch, setDebouncedExecutionSearch] = useState((execQ ?? '').trim());
  // Round-trip the debounced term through the URL so shared links carry the search
  // too; replace-state keeps typing from spamming history.
  useDebouncedEffect(
    () => {
      const trimmed = executionSearch.trim();
      setDebouncedExecutionSearch(trimmed);
      navigate({
        to: '/t/$tenantId/loom',
        params: { tenantId },
        search: (prev) => ({ ...prev, execQ: trimmed || undefined }),
        replace: true,
      });
    },
    [executionSearch],
    SEARCH_DEBOUNCE_MS,
  );

  // Resolve the preset to a concrete cutoff once per selection, not per render —
  // a per-render timestamp would change the query key on every render. A complete
  // custom date range takes precedence over the preset.
  const [startedAfter, startedBefore] = useMemo(() => {
    if (execFrom && execTo) return [localDateStartIso(execFrom), localDateEndIso(execTo)];
    return [execWithin ? startedWithinCutoffIso(execWithin) : undefined, undefined];
  }, [execWithin, execFrom, execTo]);

  const executionFilters = useMemo<RecentExecutionsFilters>(
    () => ({
      status: execStatus,
      trigger_type: execTrigger,
      workflow_id: execWorkflow,
      executed_by: execBy,
      started_after: startedAfter,
      started_before: startedBefore,
      // The API rejects search terms over 100 chars.
      search: debouncedExecutionSearch.slice(0, 100) || undefined,
    }),
    [execStatus, execTrigger, execWorkflow, execBy, startedAfter, startedBefore, debouncedExecutionSearch],
  );

  const activeTabStorageKey = useMemo(
    () => getEditorStorageKey(EDITOR_STORAGE_KEYS.ACTIVE_TAB, TLAIDomain.WORKFLOWS, 'list', tenantId),
    [tenantId],
  );

  // Read the cached tab once (in a memo, not an effect) so a return visit with
  // no `?tab=` restores the last-selected tab without a flash of the default.
  const cachedTab = useMemo(() => {
    try {
      return parseLoomTab(localStorage.getItem(activeTabStorageKey));
    } catch {
      return undefined;
    }
  }, [activeTabStorageKey]);

  // #2325 shipped `?wfStatus=ARCHIVED` deep links, and the status dropdown no longer offers
  // ARCHIVED. Honour those links by turning the Archived toggle on, and drop the value from the
  // filter either way: left in place it would query archived rows while the select rendered an
  // empty trigger, since no option matches it.
  const isLegacyArchivedLink = wfStatusParam === TLWfWorkflowStatus.ARCHIVED;
  const showArchived = wfArchived ?? isLegacyArchivedLink;
  const wfStatus = isLegacyArchivedLink ? undefined : wfStatusParam;

  // Precedence: URL param → cached tab → default (Workflows).
  const activeTab = tabParam ?? cachedTab ?? LoomTab.WORKFLOWS;

  // Inactive tab panels unmount, but these hooks live here for the URL-state sync, so each one
  // is gated on its tab: the landing page otherwise fires three list requests for one visible table.
  const {
    data: templatesData,
    isLoading: isLoadingTemplates,
    error: templatesError,
  } = useListTemplatesV2({ limit: 50 }, { query: { enabled: activeTab === LoomTab.TEMPLATES } });

  const {
    data: executionsData,
    isLoading: isLoadingExecutions,
    isFetching: isFetchingExecutions,
    isPlaceholderData: isShowingStaleExecutions,
    error: executionsError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    refetch: refetchExecutions,
  } = useListRecentWorkflowExecutionsV2Infinite(executionFilters, undefined, {
    enabled: activeTab === LoomTab.EXECUTIONS,
  });

  const templates = templatesData?.templates ?? [];

  const executions = useMemo(() => {
    return executionsData?.pages.flatMap((page) => page.items) ?? [];
  }, [executionsData]);

  const totalExecutions = executionsData?.pages[0]?.total ?? 0;

  // Persist the viewed tab so the next visit can restore it.
  useEffect(() => {
    try {
      localStorage.setItem(activeTabStorageKey, activeTab);
    } catch {
      // Ignore storage failures (private mode, quota).
    }
  }, [activeTab, activeTabStorageKey]);

  const ownershipStorageKeys = useMemo(
    () => ({
      workflows: getEditorStorageKey(
        EDITOR_STORAGE_KEYS.OWNERSHIP_FILTER,
        TLAIDomain.WORKFLOWS,
        'workflows-list',
        tenantId,
      ),
      templates: getEditorStorageKey(
        EDITOR_STORAGE_KEYS.OWNERSHIP_FILTER,
        TLAIDomain.WORKFLOWS,
        'templates-list',
        tenantId,
      ),
    }),
    [tenantId],
  );

  // Same restore pattern as the tab: URL param → cached choice → default, read
  // once in a memo so a return visit lands on the last-used filter without a
  // flash of the default.
  const cachedOwnership = useMemo(() => {
    try {
      return {
        workflows: parseOwnership(localStorage.getItem(ownershipStorageKeys.workflows)),
        templates: parseOwnership(localStorage.getItem(ownershipStorageKeys.templates)),
      };
    } catch {
      return { workflows: null, templates: null };
    }
  }, [ownershipStorageKeys]);

  const workflowOwnership = ownerParam ?? cachedOwnership.workflows ?? 'owned';
  const templateOwnership = tmplOwnerParam ?? cachedOwnership.templates ?? 'owned';

  const workflowSort: WorkflowSortState = {
    field: wfSort ?? TLWfWorkflowSortField.UPDATED_DATE,
    desc: (wfDir ?? TLSortDirection.desc) === TLSortDirection.desc,
  };
  const workflowPage = wfPage ?? 1;

  const workflowListFilters = useMemo<WorkflowListFilters>(
    () => ({
      // The API rejects search terms over 100 chars.
      search: debouncedWorkflowSearch.slice(0, 100) || undefined,
      // The Archived toggle pins the status; ?wfStatus= applies only while it is off, and its
      // dropdown no longer offers ARCHIVED.
      status: showArchived ? TLWfWorkflowStatus.ARCHIVED : wfStatus,
      created_by: wfBy,
      // The owner picker is one control: a specific owner replaces the scope rather than
      // intersecting with it, or "Owned by me" + someone else would always come back empty.
      ownership: wfBy?.length ? TLWfOwnershipScope.ALL : OWNERSHIP_SCOPE[workflowOwnership],
      sort_by: wfSort ?? TLWfWorkflowSortField.UPDATED_DATE,
      sort_dir: wfDir ?? TLSortDirection.desc,
    }),
    [debouncedWorkflowSearch, showArchived, wfStatus, wfBy, workflowOwnership, wfSort, wfDir],
  );

  const {
    data: workflowsData,
    isLoading: isLoadingWorkflows,
    isPlaceholderData: isShowingStaleWorkflows,
    error: workflowsError,
  } = useListWorkflowsV2Filtered(
    {
      ...workflowListFilters,
      limit: WORKFLOWS_PAGE_SIZE,
      offset: (workflowPage - 1) * WORKFLOWS_PAGE_SIZE,
    },
    { staleTime: WORKFLOWS_LIST_STALE_MS, enabled: activeTab === LoomTab.WORKFLOWS },
  );

  const workflows = workflowsData?.workflows ?? [];
  const totalWorkflows = workflowsData?.total ?? 0;

  // Clamp an out-of-range page back to the last valid one, so deleting the last page's
  // rows (or a stale ?wfPage= deep link) doesn't strand the user on an empty page.
  useEffect(() => {
    if (isLoadingWorkflows || totalWorkflows === 0) return;
    const lastPage = Math.ceil(totalWorkflows / WORKFLOWS_PAGE_SIZE);
    if (workflowPage <= lastPage) return;
    navigate({
      to: '/t/$tenantId/loom',
      params: { tenantId },
      search: (prev) => ({ ...prev, wfPage: lastPage === 1 ? undefined : lastPage }),
      replace: true,
    });
  }, [isLoadingWorkflows, totalWorkflows, workflowPage, navigate, tenantId]);

  const workflowFilterValues: WorkflowFilterValues = {
    status: wfStatus,
    createdBy: wfBy,
  };

  const handleWorkflowFiltersChange = (patch: Partial<WorkflowFilterValues>) => {
    navigate({
      to: '/t/$tenantId/loom',
      params: { tenantId },
      search: (prev) => ({
        ...prev,
        ...('status' in patch ? { wfStatus: patch.status } : {}),
        ...('createdBy' in patch ? { wfBy: patch.createdBy } : {}),
        wfPage: undefined,
      }),
      replace: true,
    });
  };

  const handleWorkflowSortChange = (next: WorkflowSortState) => {
    navigate({
      to: '/t/$tenantId/loom',
      params: { tenantId },
      search: (prev) => ({
        ...prev,
        wfSort: next.field,
        wfDir: next.desc ? TLSortDirection.desc : TLSortDirection.asc,
        wfPage: undefined,
      }),
      replace: true,
    });
  };

  const queryClient = useQueryClient();
  const handleOwnershipHover = (value: WorkflowOwnershipFilterValue) => {
    if (value === workflowOwnership && !wfBy?.length) return;
    void prefetchListWorkflowsV2Query(
      queryClient,
      {
        ...workflowListFilters,
        ownership: OWNERSHIP_SCOPE[value],
        created_by: undefined,
        limit: WORKFLOWS_PAGE_SIZE,
        offset: 0,
      },
      { query: { staleTime: WORKFLOWS_LIST_STALE_MS } },
    );
  };

  const handleWorkflowPageChange = (nextPage: number) => {
    navigate({
      to: '/t/$tenantId/loom',
      params: { tenantId },
      search: (prev) => ({ ...prev, wfPage: nextPage === 1 ? undefined : nextPage }),
      replace: true,
    });
  };

  useEffect(() => {
    try {
      localStorage.setItem(ownershipStorageKeys.workflows, workflowOwnership);
      localStorage.setItem(ownershipStorageKeys.templates, templateOwnership);
    } catch {
      // Ignore storage failures (private mode, quota).
    }
  }, [workflowOwnership, templateOwnership, ownershipStorageKeys]);

  // Reflect a cache-restored tab into the URL so it matches the shown tab.
  const didReflectCachedTabRef = useRef(false);
  useEffect(() => {
    if (didReflectCachedTabRef.current) return;
    didReflectCachedTabRef.current = true;
    if (!tabParam && cachedTab) {
      navigate({
        to: '/t/$tenantId/loom',
        params: { tenantId },
        search: (prev) => ({ ...prev, tab: cachedTab }),
        replace: true,
      });
    }
  }, [tabParam, cachedTab, navigate, tenantId]);

  // Normalize a legacy ?wfStatus=ARCHIVED link onto ?wfArchived once, so showArchived stops
  // deriving from a param the filter bar can clear — otherwise Clear drops wfStatus and
  // silently exits the archive instead of just clearing filters. Writing the resolved
  // showArchived keeps ?wfArchived=false winning over the legacy value, as the ?? did.
  const didNormalizeLegacyArchivedRef = useRef(false);
  useEffect(() => {
    if (didNormalizeLegacyArchivedRef.current) return;
    didNormalizeLegacyArchivedRef.current = true;
    if (!isLegacyArchivedLink) return;
    navigate({
      to: '/t/$tenantId/loom',
      params: { tenantId },
      search: (prev) => ({ ...prev, wfArchived: showArchived || undefined, wfStatus: undefined }),
      replace: true,
    });
  }, [isLegacyArchivedLink, showArchived, navigate, tenantId]);

  const handleArchivedViewChange = (archived: boolean) => {
    navigate({
      to: '/t/$tenantId/loom',
      params: { tenantId },
      search: (prev) => ({
        ...prev,
        wfArchived: archived || undefined,
        // The live and archived views have different row counts, so page N rarely survives
        // the switch; the clamp effect would correct it after a wasted fetch.
        wfPage: undefined,
        // A retired ?wfStatus=ARCHIVED link must not linger once the toggle owns the status.
        wfStatus: prev.wfStatus === TLWfWorkflowStatus.ARCHIVED ? undefined : prev.wfStatus,
      }),
      replace: true,
    });
  };

  const handleTabChange = (value: LoomTab) => {
    navigate({
      to: '/t/$tenantId/loom',
      params: { tenantId },
      search: (prev) => ({
        ...prev,
        tab: value,
      }),
    });
  };

  const handleOwnershipChange = (value: WorkflowOwnershipFilterValue) => {
    navigate({
      to: '/t/$tenantId/loom',
      params: { tenantId },
      search: (prev) => ({ ...prev, owner: value, wfBy: undefined, wfPage: undefined }),
      replace: true,
    });
  };

  const handleTemplateOwnershipChange = (value: WorkflowOwnershipFilterValue) => {
    navigate({
      to: '/t/$tenantId/loom',
      params: { tenantId },
      search: (prev) => ({ ...prev, tmplOwner: value, tmplBy: undefined }),
      replace: true,
    });
  };

  const handleTemplateCreatedByChange = (emails: string[]) => {
    navigate({
      to: '/t/$tenantId/loom',
      params: { tenantId },
      search: (prev) => ({ ...prev, tmplBy: emails.length ? emails : undefined }),
      replace: true,
    });
  };

  const workflowsTableProps = {
    workflows,
    totalCount: totalWorkflows,
    isLoading: isLoadingWorkflows,
    isFetching: isShowingStaleWorkflows,
    error: workflowsError,
    page: workflowPage,
    pageSize: WORKFLOWS_PAGE_SIZE,
    onPageChange: handleWorkflowPageChange,
    search: workflowSearch,
    onSearchChange: setWorkflowSearch,
    filters: workflowFilterValues,
    onFiltersChange: handleWorkflowFiltersChange,
    ownership: workflowOwnership,
    onOwnershipChange: handleOwnershipChange,
    onOwnershipHover: handleOwnershipHover,
    sort: workflowSort,
    onSortChange: handleWorkflowSortChange,
    isArchivedView: showArchived,
    onArchivedViewChange: handleArchivedViewChange,
  };

  const executionFilterValues: ExecutionFilterValues = {
    status: execStatus,
    trigger: execTrigger,
    workflowId: execWorkflow,
    executedBy: execBy,
    startedWithin: execWithin,
    startedFrom: execFrom,
    startedTo: execTo,
  };

  const handleExecutionFiltersChange = (patch: Partial<ExecutionFilterValues>) => {
    navigate({
      to: '/t/$tenantId/loom',
      params: { tenantId },
      search: (prev) => ({
        ...prev,
        ...('status' in patch ? { execStatus: patch.status } : {}),
        ...('trigger' in patch ? { execTrigger: patch.trigger } : {}),
        ...('workflowId' in patch ? { execWorkflow: patch.workflowId } : {}),
        ...('executedBy' in patch ? { execBy: patch.executedBy } : {}),
        ...('startedWithin' in patch ? { execWithin: patch.startedWithin } : {}),
        ...('startedFrom' in patch ? { execFrom: patch.startedFrom } : {}),
        ...('startedTo' in patch ? { execTo: patch.startedTo } : {}),
      }),
      replace: true,
    });
  };

  return (
    <div className="flex size-full flex-col gap-3.5 overflow-hidden p-6">
      <title>{activeTab}</title>
      <FeatureHeader title={activeTab} Icon={Network}>
        {LOOM_TAB_DESCRIPTION[activeTab]}
      </FeatureHeader>
      <LoomTabs tab={activeTab} onTabChange={handleTabChange} className="flex min-h-0 flex-1 flex-col gap-3">
        {!newNavigationEnabled && (
          <LoomTabs.List className="flex w-fit gap-0.5 rounded-lg bg-tlai-3/90 p-1 text-sm">
            <LoomTabs.Trigger value={LoomTab.WORKFLOWS} className="px-3 py-1">
              {LoomTab.WORKFLOWS}
            </LoomTabs.Trigger>
            <LoomTabs.Trigger value={LoomTab.TEMPLATES} className="px-3 py-1">
              {LoomTab.TEMPLATES}
            </LoomTabs.Trigger>
            <LoomTabs.Trigger value={LoomTab.EXECUTIONS} className="px-3 py-1">
              {LoomTab.EXECUTIONS}
            </LoomTabs.Trigger>
          </LoomTabs.List>
        )}

        <LoomTabs.Content value={LoomTab.WORKFLOWS}>
          <WorkflowsTable {...workflowsTableProps} />
        </LoomTabs.Content>

        <LoomTabs.Content value={LoomTab.EXECUTIONS}>
          <LoomExecutionsTable
            executions={executions}
            totalCount={totalExecutions}
            isLoading={isLoadingExecutions}
            isRefetching={isFetchingExecutions && isShowingStaleExecutions}
            error={executionsError}
            onRefresh={() => refetchExecutions()}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={() => fetchNextPage()}
            search={executionSearch}
            onSearchChange={setExecutionSearch}
            filters={executionFilterValues}
            onFiltersChange={handleExecutionFiltersChange}
          />
        </LoomTabs.Content>

        <LoomTabs.Content value={LoomTab.TEMPLATES}>
          <TemplatesTable
            templates={templates}
            isLoading={isLoadingTemplates}
            error={templatesError}
            ownership={templateOwnership}
            onOwnershipChange={handleTemplateOwnershipChange}
            createdBy={tmplBy}
            onCreatedByChange={handleTemplateCreatedByChange}
          />
        </LoomTabs.Content>
      </LoomTabs>
    </div>
  );
}
